// Refund part or all of one Stripe card payment. The second step of a two-step
// refund: the payment row's "Refund…" button opens this; only the confirm refunds.
//
// The confirm restates the payment (date, amount, what is still refundable), the
// amount going back to the card, and needs a reason — which stays in Atlas, on the
// payment's notes, and is never sent to Stripe (A's service). A refund larger than
// what is refundable is refused here and by the server.
//
// Same one-click rules as the charge: disabled in flight, a ref guard, one request
// id per attempt, and no retry after an answer that may have moved money. Posts
// nothing to the ledger: the reversal appears when Stripe's webhook lands.

import React, { useEffect, useRef, useState } from "react";
import { formatCurrency } from "../../utils/formatCurrency";
import {
  centsToDollars,
  dollarsToCents,
  formatDay,
  newRequestId,
  refundPayment,
} from "./api";
import "./Collections.css";

const REASON_MAX = 200;

export default function RefundModal({
  practiceId,
  patientId,
  payment,
  onClose,
  onRefunded,
}) {
  // A refund already asked for on this payment has not necessarily landed: Stripe
  // reduces the payment only when its webhook posts. The refundable figure below is
  // then too high, so nothing is prefilled and the biller has to say they checked.
  const stale = payment.refundRequested === true;
  const [amountText, setAmountText] = useState(
    payment.refundableCents > 0 && !stale
      ? (payment.refundableCents / 100).toFixed(2)
      : "",
  );
  const [acknowledged, setAcknowledged] = useState(!stale);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [settled, setSettled] = useState(false);
  const [message, setMessage] = useState(null);
  const inFlight = useRef(false);
  const attempted = useRef(false);
  const requestId = useRef(null);
  const reasonRef = useRef(null);

  useEffect(() => {
    reasonRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !inFlight.current) onClose(attempted.current);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const amountCents = dollarsToCents(amountText);
  const amountValid =
    amountCents != null &&
    !Number.isNaN(amountCents) &&
    amountCents > 0 &&
    amountCents <= payment.refundableCents;
  const reasonValid =
    reason.trim().length > 0 && reason.trim().length <= REASON_MAX;
  const canConfirm =
    payment.refundableCents > 0 &&
    amountValid &&
    reasonValid &&
    acknowledged &&
    !busy &&
    !settled;

  const confirm = async () => {
    if (!canConfirm || inFlight.current) return;
    let id;
    try {
      id = requestId.current || newRequestId();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
      return;
    }
    requestId.current = id;
    inFlight.current = true;
    attempted.current = true;
    setBusy(true);
    setMessage(null);
    try {
      const result = await refundPayment(practiceId, patientId, payment.id, {
        amountCents,
        reason: reason.trim(),
        requestId: id,
      });
      setSettled(true);
      onRefunded(result, { amountCents });
    } catch (err) {
      if ([400, 403, 404, 422, 429, 503].includes(err.status)) {
        // Refused before Stripe was asked: nothing refunded. A new id for the next try.
        requestId.current = null;
        setMessage({ type: "error", text: err.message });
      } else if (err.status === 409) {
        // The payment is no longer posted; this dialog's numbers are stale.
        setSettled(true);
        setMessage({ type: "warn", text: err.message });
      } else {
        setSettled(true);
        setMessage({
          type: "error",
          text: "No clear answer from Stripe, so this refund may or may not have been made. Close this and check the card payments before trying again.",
        });
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const amountLabel = amountValid
    ? formatCurrency(centsToDollars(amountCents))
    : "—";

  return (
    <div
      className="coll-modal-backdrop"
      onClick={() => !inFlight.current && onClose(attempted.current)}
    >
      <div
        className="coll-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coll-refund-title"
      >
        <div className="coll-modal-header">
          <h2 id="coll-refund-title">Refund a card payment</h2>
          <button
            className="coll-modal-close"
            onClick={() => onClose(attempted.current)}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="coll-modal-body">
          <p className="coll-modal-lede">
            The money goes back to the card the patient paid with. The ledger
            shows the reversal when Stripe confirms the refund.
          </p>

          <dl className="coll-confirm-facts" data-testid="refund-facts">
            <dt>Payment</dt>
            <dd>
              {formatDay(payment.paymentDate)} ·{" "}
              {formatCurrency(centsToDollars(payment.amountCents))}
            </dd>
            <dt>Refundable</dt>
            <dd>
              {formatCurrency(centsToDollars(payment.refundableCents))}
              {stale ? " (may be out of date)" : ""}
            </dd>
          </dl>

          {stale && (
            <div className="coll-message warn" role="alert">
              <div>
                <strong>A refund was already requested on this payment.</strong>{" "}
                Stripe may not have applied it yet, so the figure above can be too
                high and a second refund here would be a second refund to the
                patient. Check Stripe, or wait for the payment to drop, before
                refunding again.
                <label className="coll-check">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    disabled={busy || settled}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                  <span>I checked: this payment still owes this refund.</span>
                </label>
              </div>
            </div>
          )}

          {payment.refundableCents <= 0 ? (
            <div className="coll-message error" role="alert">
              Nothing on this payment can be refunded.
            </div>
          ) : (
            <>
              <label className="coll-field">
                <span>Amount to refund ($)</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amountText}
                  disabled={busy || settled}
                  onChange={(e) => setAmountText(e.target.value)}
                />
                {!amountValid && amountText !== "" && (
                  <small className="coll-field-error">
                    Enter an amount above $0 and no more than{" "}
                    {formatCurrency(centsToDollars(payment.refundableCents))}.
                  </small>
                )}
              </label>
              <label className="coll-field">
                <span>Reason</span>
                <textarea
                  ref={reasonRef}
                  rows={2}
                  maxLength={REASON_MAX}
                  value={reason}
                  disabled={busy || settled}
                  placeholder="e.g. overpaid; charged twice for 9/3"
                  onChange={(e) => setReason(e.target.value)}
                />
                <small className="coll-field-hint">
                  Kept on the payment in Atlas. Not sent to Stripe.
                </small>
              </label>
            </>
          )}

          {message && (
            <div className={`coll-message ${message.type}`} role="alert">
              {message.text}
            </div>
          )}
        </div>

        <div className="coll-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onClose(attempted.current)}
            disabled={busy}
          >
            {settled ? "Close" : "Cancel"}
          </button>
          <button
            className="btn btn-primary btn-sm coll-money-btn"
            onClick={confirm}
            disabled={!canConfirm}
          >
            {busy ? "Refunding…" : `Refund ${amountLabel} to the card`}
          </button>
        </div>
      </div>
    </div>
  );
}
